import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, X, Send, Check, Pencil, AlertTriangle, ChevronUp, ChevronDown, Loader2, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { useHasPermission } from "../contexts/PermissionsContext";
import { toast } from "sonner";

const ACTION_TYPE_COLOR = {
    payroll_line_adjustment: "bg-emerald-50 text-emerald-700 border-emerald-200",
    attendance_event: "bg-sky-50 text-sky-700 border-sky-200",
    leave_request: "bg-amber-50 text-amber-700 border-amber-200",
    task_assignment: "bg-violet-50 text-violet-700 border-violet-200",
};

const ACTION_TYPE_LABEL = {
    payroll_line_adjustment: "Payroll adjustment",
    attendance_event: "Attendance",
    leave_request: "Leave",
    task_assignment: "Task",
};

const STATUS_BADGE = {
    proposed: "bg-amber-50 text-amber-700 border-amber-200",
    applied: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-gray-100 text-gray-600 border-gray-200",
    failed: "bg-rose-50 text-rose-700 border-rose-200",
};

function ConfidencePill({ value }) {
    const pct = Math.round((value || 0) * 100);
    let cls = "bg-rose-100 text-rose-800";
    if (pct >= 80) cls = "bg-emerald-100 text-emerald-800";
    else if (pct >= 60) cls = "bg-amber-100 text-amber-800";
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{pct}%</span>;
}

function ActionRow({ action, idx }) {
    const cls = ACTION_TYPE_COLOR[action.type] || "bg-gray-50 text-gray-600 border-gray-200";
    return (
        <div className={`text-xs rounded border px-2 py-1.5 ${cls}`} data-testid={`ai-action-${idx}`}>
            <div className="font-semibold uppercase tracking-wider mb-1">{ACTION_TYPE_LABEL[action.type] || action.type}</div>
            <div className="space-y-0.5 mono text-[11px]">
                {Object.entries(action).filter(([k]) => k !== "type").map(([k, v]) => (
                    <div key={k}><span className="opacity-60">{k}:</span> <span className="font-medium">{String(v ?? "—")}</span></div>
                ))}
            </div>
        </div>
    );
}

/**
 * Floating AI assistant panel — collapsible card on the right side of dashboards.
 * Props:
 *  - moduleId?: string  (current module — for transaction proposes)
 *  - moduleCode?: string  (for label)
 *  - allowModulePicker?: bool (Master Finance only)
 *  - modules?: array (when allowModulePicker)
 */
export default function AIPanel({ moduleId, moduleCode, allowModulePicker = false, modules = [] }) {
    const canPropose = useHasPermission("ai.transaction_propose", moduleId);
    const canApprove = useHasPermission("ai.transaction_approve", moduleId);

    const [open, setOpen] = useState(true);
    const [text, setText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [proposal, setProposal] = useState(null); // { interaction_id, parsed_actions, overall_confidence, warnings }
    const [editing, setEditing] = useState(false);
    const [editedJson, setEditedJson] = useState("");
    const [recent, setRecent] = useState([]);
    const [pickModuleId, setPickModuleId] = useState(moduleId || "");
    const [lastError, setLastError] = useState(null);

    useEffect(() => { setPickModuleId(moduleId || ""); }, [moduleId]);

    const reloadHistory = async () => {
        try {
            const { data } = await api.get("/ai/interactions", { params: { feature: "transaction_entry", limit: 5 } });
            setRecent(data);
        } catch (_e) { /* ignore */ }
    };

    useEffect(() => { if (canPropose) reloadHistory(); }, [canPropose]);

    if (!canPropose) return null;

    const onSubmit = async () => {
        if (!text.trim()) return;
        setSubmitting(true);
        setLastError(null);
        try {
            const body = { text: text.trim() };
            if (pickModuleId) body.module_id = pickModuleId;
            const { data } = await api.post("/ai/transaction/propose", body);
            setProposal(data);
        } catch (e) {
            const msg = formatApiError(e);
            setLastError(msg);
            toast.error(msg);
        }
        finally { setSubmitting(false); }
    };

    const onApprove = async () => {
        if (!proposal) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(`/ai/transaction/${proposal.interaction_id}/approve`);
            if (data.status === "applied" && data.errors?.length === 0) {
                toast.success(`Applied ${data.created.length} action(s)`);
            } else if (data.errors?.length) {
                toast.error(`Some actions failed: ${data.errors.map(e => e.error).join("; ")}`);
            } else {
                toast.error("Nothing was applied.");
            }
            setText(""); setProposal(null); reloadHistory();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSubmitting(false); }
    };

    const onReject = async () => {
        if (!proposal) return;
        try { await api.post(`/ai/transaction/${proposal.interaction_id}/reject`, { note: "user rejected" }); }
        catch (e) { toast.error(formatApiError(e)); }
        setText(""); setProposal(null); reloadHistory();
    };

    const onSaveEdit = async () => {
        try {
            const arr = JSON.parse(editedJson);
            if (!Array.isArray(arr)) throw new Error("Expected array");
            await api.post(`/ai/transaction/${proposal.interaction_id}/edit`, { parsed_actions: arr });
            setProposal({ ...proposal, parsed_actions: arr });
            setEditing(false);
        } catch (e) { toast.error(`Invalid JSON: ${e.message}`); }
    };

    const conf = proposal?.overall_confidence || 0;
    const warningIsLowConf = conf < 0.6;

    return (
        <div className="asl-card overflow-hidden" data-testid="ai-panel" style={{ borderTop: "3px solid var(--asl-crimson)" }}>
            <button
                type="button"
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                onClick={() => setOpen((v) => !v)}
                data-testid="ai-panel-toggle"
            >
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 asl-crimson-text" />
                    <span className="text-sm font-semibold asl-navy-text">Ask AI to enter a transaction</span>
                </div>
                {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {open && (
                <div className="p-4 border-t" style={{ borderColor: "var(--asl-border)" }}>
                    {allowModulePicker && (
                        <div className="mb-2">
                            <Select value={pickModuleId} onValueChange={setPickModuleId}>
                                <SelectTrigger className="h-8 text-xs" data-testid="ai-module-picker">
                                    <SelectValue placeholder="Pick target module…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <Textarea
                        rows={3}
                        placeholder='Try: "Pay Joseph Kimani $50 overtime bonus for last Saturday" or "Mark John Smith on sick leave Mar 5 to Mar 7"'
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="text-sm"
                        data-testid="ai-textarea"
                    />
                    {lastError && (
                        <div className="mt-2 flex items-start gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 px-2 py-1.5 rounded" data-testid="ai-error">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <div className="font-semibold">AI request failed</div>
                                <div className="opacity-90 break-words">{lastError}</div>
                            </div>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-700 hover:bg-rose-100" onClick={onSubmit} disabled={submitting || !text.trim()} data-testid="ai-retry">
                                <RefreshCw className="h-3 w-3 mr-1" /> Try again
                            </Button>
                        </div>
                    )}
                    <div className="flex justify-end mt-2">
                        <Button
                            size="sm"
                            onClick={onSubmit}
                            disabled={submitting || !text.trim()}
                            className="asl-navy-bg text-white"
                            data-testid="ai-submit"
                        >
                            {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                            {submitting ? "Thinking…" : "Ask AI"}
                        </Button>
                    </div>

                    {proposal && (
                        <div className="mt-3 space-y-2 border-t pt-3" style={{ borderColor: "var(--asl-border)" }} data-testid="ai-proposal">
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]">Proposed action(s)</span>
                                <ConfidencePill value={conf} />
                            </div>
                            {!editing && proposal.parsed_actions.map((a, i) => <ActionRow key={i} action={a} idx={i} />)}
                            {editing && (
                                <Textarea rows={10} value={editedJson} onChange={(e) => setEditedJson(e.target.value)} className="mono text-[11px]" data-testid="ai-edit-json" />
                            )}
                            {warningIsLowConf && (
                                <div className="flex items-start gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 px-2 py-1.5 rounded" data-testid="ai-low-conf">
                                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold">Low confidence — please verify before approving</div>
                                        <div className="opacity-80">Overall confidence is {Math.round(conf * 100)}% (below 60%). Review each action carefully.</div>
                                    </div>
                                </div>
                            )}
                            {(proposal.warnings || []).map((w, i) => (
                                <div key={i} className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded" data-testid={`ai-warning-${i}`}>
                                    <strong>{w.field}:</strong> {w.message}
                                    {w.candidates && w.candidates.length > 0 && <div className="mono text-[10px] mt-0.5">candidates: {w.candidates.join(", ")}</div>}
                                </div>
                            ))}
                            <div className="flex items-center justify-end gap-2 pt-1">
                                {!editing ? (
                                    <Button size="sm" variant="outline" onClick={() => { setEditing(true); setEditedJson(JSON.stringify(proposal.parsed_actions, null, 2)); }} data-testid="ai-edit-btn">
                                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                                    </Button>
                                ) : (
                                    <Button size="sm" variant="outline" onClick={onSaveEdit} data-testid="ai-edit-save">Save edits</Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={onReject} data-testid="ai-reject"><X className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                                {canApprove && (
                                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onApprove} disabled={editing} data-testid="ai-approve">
                                        <Check className="h-3.5 w-3.5 mr-1" /> Approve
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {recent.length > 0 && (
                        <div className="mt-3 border-t pt-3" style={{ borderColor: "var(--asl-border)" }}>
                            <div className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)] mb-1">Recent</div>
                            <div className="space-y-1" data-testid="ai-recent-list">
                                {recent.map((r) => (
                                    <div key={r.id} className="flex items-center justify-between text-xs gap-2" data-testid={`ai-recent-${r.id}`}>
                                        <span className="truncate flex-1 text-[color:var(--asl-muted)]" title={r.prompt}>{r.prompt}</span>
                                        <Badge className={`text-[10px] ${STATUS_BADGE[r.status] || ""}`}>{r.status}</Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
